export interface GlobalEvent {
  id: string;
  eventName: string;
  date: string; // DD/MM format
  isRecurringAnnual: boolean;
  targetAudience: 'All' | 'Teachers' | 'Parents';
  description: string;
  color: string; // blue theme
  type: 'global';
}

export interface SchoolHoliday {
  id: string;
  holidayTitle: string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  holidayType: 'National' | 'Regional' | 'Discretionary';
  impactsAttendance: boolean;
  description?: string;
  color: string; // red/orange theme
  type: 'holiday';
}

export interface PersonalTask {
  id: string;
  title: string;
  date: string; // YYYY-MM-DD
  dueTime?: string; // HH:MM
  description?: string;
  completed: boolean;
  color: string; // green theme
  type: 'task';
}

export type CalendarEntry = GlobalEvent | SchoolHoliday | PersonalTask;

export const mockGlobalEvents: GlobalEvent[] = [
  {
    id: 'ge-001',
    eventName: 'World Autism Awareness Day',
    date: '02/04',
    isRecurringAnnual: true,
    targetAudience: 'All',
    description: 'A United Nations sanctioned day to raise awareness about autism spectrum disorder and promote inclusion and acceptance.',
    color: '#3B82F6',
    type: 'global',
  },
  {
    id: 'ge-002',
    eventName: "International Children's Day",
    date: '01/06',
    isRecurringAnnual: true,
    targetAudience: 'All',
    description: 'Celebrated worldwide to honor children, promote their rights and celebrate their potential. Special activities planned in all classrooms.',
    color: '#3B82F6',
    type: 'global',
  },
  {
    id: 'ge-003',
    eventName: 'World Mental Health Day',
    date: '10/10',
    isRecurringAnnual: true,
    targetAudience: 'All',
    description: 'Global day dedicated to mental health education, awareness, and advocacy against social stigma.',
    color: '#3B82F6',
    type: 'global',
  },
  {
    id: 'ge-004',
    eventName: 'International Day of Persons with Disabilities',
    date: '03/12',
    isRecurringAnnual: true,
    targetAudience: 'All',
    description: 'Promotes the rights and well-being of persons with disabilities in all spheres of society.',
    color: '#3B82F6',
    type: 'global',
  },
  {
    id: 'ge-005',
    eventName: "World Teachers' Day",
    date: '05/10',
    isRecurringAnnual: true,
    targetAudience: 'Teachers',
    description: 'Celebrating the contribution of teachers worldwide. Special appreciation ceremony planned for all faculty members.',
    color: '#3B82F6',
    type: 'global',
  },
  {
    id: 'ge-006',
    eventName: 'Down Syndrome Awareness Day',
    date: '21/03',
    isRecurringAnnual: true,
    targetAudience: 'All',
    description: 'A global awareness day observed to create a single voice advocating for the rights, inclusion, and well-being of people with Down syndrome.',
    color: '#3B82F6',
    type: 'global',
  },
  {
    id: 'ge-007',
    eventName: 'World Speech & Language Day',
    date: '06/03',
    isRecurringAnnual: true,
    targetAudience: 'All',
    description: 'Highlighting the importance of speech and language therapy. Open house for parents to meet our speech-language pathologists.',
    color: '#3B82F6',
    type: 'global',
  },
  {
    id: 'ge-008',
    eventName: 'International Day of Education',
    date: '24/01',
    isRecurringAnnual: true,
    targetAudience: 'All',
    description: 'Celebrating the role of education for peace and development. Special assembly and student presentations throughout the day.',
    color: '#3B82F6',
    type: 'global',
  },
  {
    id: 'ge-009',
    eventName: 'World Cerebral Palsy Day',
    date: '06/10',
    isRecurringAnnual: true,
    targetAudience: 'All',
    description: 'A global movement of people with cerebral palsy, their families, and organizations that support them.',
    color: '#3B82F6',
    type: 'global',
  },
  {
    id: 'ge-010',
    eventName: 'National Education Day',
    date: '11/11',
    isRecurringAnnual: true,
    targetAudience: 'All',
    description: 'Commemorating the birth anniversary of Maulana Abul Kalam Azad, the first Education Minister of independent India.',
    color: '#3B82F6',
    type: 'global',
  },
  {
    id: 'ge-011',
    eventName: 'Parent Workshop: Emotional Regulation',
    date: '12/08',
    isRecurringAnnual: false,
    targetAudience: 'Parents',
    description: 'A hands-on workshop teaching parents evidence-based strategies for supporting emotional regulation at home.',
    color: '#3B82F6',
    type: 'global',
  },
  {
    id: 'ge-012',
    eventName: 'Faculty CPR & First Aid Training',
    date: '18/08',
    isRecurringAnnual: false,
    targetAudience: 'Teachers',
    description: 'Mandatory CPR and first aid certification renewal for all teaching and therapy staff. Conducted by certified Red Cross trainers.',
    color: '#3B82F6',
    type: 'global',
  },
];

export const mockSchoolHolidays: SchoolHoliday[] = [
  {
    id: 'sh-001',
    holidayTitle: 'Independence Day',
    startDate: '2026-08-15',
    endDate: '2026-08-15',
    holidayType: 'National',
    impactsAttendance: true,
    description: 'National holiday commemorating independence. School remains closed.',
    color: '#EF4444',
    type: 'holiday',
  },
  {
    id: 'sh-002',
    holidayTitle: 'Raksha Bandhan',
    startDate: '2026-08-20',
    endDate: '2026-08-20',
    holidayType: 'Regional',
    impactsAttendance: true,
    description: 'Festival celebrating the bond between brothers and sisters.',
    color: '#EF4444',
    type: 'holiday',
  },
  {
    id: 'sh-003',
    holidayTitle: 'Janmashtami',
    startDate: '2026-08-25',
    endDate: '2026-08-25',
    holidayType: 'Regional',
    impactsAttendance: true,
    description: 'Celebration of the birth of Lord Krishna. Cultural activities planned for the preceding day.',
    color: '#EF4444',
    type: 'holiday',
  },
  {
    id: 'sh-004',
    holidayTitle: 'Teacher Training Day',
    startDate: '2026-08-28',
    endDate: '2026-08-28',
    holidayType: 'Discretionary',
    impactsAttendance: true,
    description: 'Internal professional development. No classes — students stay home.',
    color: '#F97316',
    type: 'holiday',
  },
  {
    id: 'sh-005',
    holidayTitle: 'Mid-Term Break',
    startDate: '2026-09-15',
    endDate: '2026-09-18',
    holidayType: 'Discretionary',
    impactsAttendance: true,
    description: 'Mid-term break for students. Staff development workshops on Sep 16-17.',
    color: '#F97316',
    type: 'holiday',
  },
  {
    id: 'sh-006',
    holidayTitle: 'Gandhi Jayanti',
    startDate: '2026-10-02',
    endDate: '2026-10-02',
    holidayType: 'National',
    impactsAttendance: true,
    description: 'Birth anniversary of Mahatma Gandhi. Peace and non-violence assembly in the morning.',
    color: '#EF4444',
    type: 'holiday',
  },
  {
    id: 'sh-007',
    holidayTitle: 'Dussehra',
    startDate: '2026-10-19',
    endDate: '2026-10-19',
    holidayType: 'National',
    impactsAttendance: true,
    description: 'Victory of good over evil. School remains closed.',
    color: '#EF4444',
    type: 'holiday',
  },
  {
    id: 'sh-008',
    holidayTitle: 'Diwali Break',
    startDate: '2026-11-07',
    endDate: '2026-11-11',
    holidayType: 'National',
    impactsAttendance: true,
    description: 'Festival of lights — extended break. School reopens on November 12th.',
    color: '#EF4444',
    type: 'holiday',
  },
  {
    id: 'sh-009',
    holidayTitle: 'Winter Vacation',
    startDate: '2026-12-22',
    endDate: '2027-01-05',
    holidayType: 'Discretionary',
    impactsAttendance: true,
    description: 'End-of-year winter break. School reopens on January 6th, 2027.',
    color: '#F97316',
    type: 'holiday',
  },
  {
    id: 'sh-010',
    holidayTitle: 'Republic Day',
    startDate: '2027-01-26',
    endDate: '2027-01-26',
    holidayType: 'National',
    impactsAttendance: true,
    description: 'National holiday. Flag hoisting ceremony on campus.',
    color: '#EF4444',
    type: 'holiday',
  },
];

export const mockPersonalTasks: PersonalTask[] = [
  {
    id: 'pt-001',
    title: 'Prepare sensory activity materials for Group B',
    date: '2026-07-30',
    dueTime: '09:00',
    description: 'Organize tactile boards, textured balls, and weighted blankets for morning sensory integration session.',
    completed: false,
    color: '#22C55E',
    type: 'task',
  },
  {
    id: 'pt-002',
    title: 'IEP Review Meeting - Aarav Mehta',
    date: '2026-07-31',
    dueTime: '14:00',
    description: 'Quarterly IEP review with parents and speech therapist. Prepare progress charts and new goal proposals.',
    completed: false,
    color: '#22C55E',
    type: 'task',
  },
  {
    id: 'pt-003',
    title: 'Submit therapy session notes for July',
    date: '2026-08-01',
    dueTime: '16:00',
    description: 'Compile and submit all occupational therapy session notes for July. Must include progress metrics.',
    completed: false,
    color: '#22C55E',
    type: 'task',
  },
  {
    id: 'pt-004',
    title: 'Order new AAC devices for non-verbal students',
    date: '2026-08-03',
    dueTime: '10:00',
    description: 'Procurement request for 5 new AAC tablet devices with communication apps pre-installed.',
    completed: false,
    color: '#22C55E',
    type: 'task',
  },
  {
    id: 'pt-005',
    title: 'Arrange classroom seating for new enrolments',
    date: '2026-08-05',
    dueTime: '11:30',
    description: 'Reorganize Pre-Primary B classroom layout to accommodate 3 new students joining next week.',
    completed: true,
    color: '#22C55E',
    type: 'task',
  },
  {
    id: 'pt-006',
    title: 'Monthly staff meeting presentation',
    date: '2026-08-03',
    dueTime: '15:30',
    description: 'Prepare 15-minute presentation on new sensory integration room protocols for the monthly staff meeting.',
    completed: false,
    color: '#22C55E',
    type: 'task',
  },
  {
    id: 'pt-007',
    title: 'Parent communication — Priya Sharma progress update',
    date: '2026-07-30',
    dueTime: '13:00',
    description: 'Call Mrs. Sharma to discuss Priya\'s improvement in fine motor skills and next therapy milestones.',
    completed: false,
    color: '#22C55E',
    type: 'task',
  },
  {
    id: 'pt-008',
    title: 'Update classroom visual schedules for August',
    date: '2026-07-31',
    dueTime: '08:30',
    description: 'Print and laminate new visual schedule cards reflecting August timetable changes. Post in all 4 classrooms.',
    completed: true,
    color: '#22C55E',
    type: 'task',
  },
];

export const allCalendarEntries: CalendarEntry[] = [
  ...mockGlobalEvents,
  ...mockSchoolHolidays,
  ...mockPersonalTasks,
];